<script setup lang="ts">
import { ref } from 'vue';

import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';

import { StaySelectionType } from '../../models';
import type {
  HotelDetailsFullscreenMasonryGalleryData,
  Stay,
  StayId,
} from '../../models';

import HotelStay from './HotelStay.vue';

interface HotelDetailsStaysSectionProps {
  alternativeStays: Stay[];
  source: HotelDetailsEventSource;
  ingredientId: string;
  selectedStay?: Stay;
}
const props = defineProps<HotelDetailsStaysSectionProps>();

interface HotelDetailsStaysSectionEmits {
  (eventName: 'stay-selected', stayId: StayId): void;
  (eventName: 'more-information', stay: Stay): void;
  (
    eventName: 'open-gallery',
    data: HotelDetailsFullscreenMasonryGalleryData
  ): void;
}

const emit = defineEmits<HotelDetailsStaysSectionEmits>();

const root = ref<HTMLElement>();

defineExpose({
  root,
});
</script>

<template>
  <div id="hotel-details-stays" ref="root" class="hotel-details-stays-section">
    <div v-if="props.selectedStay" class="hotel-stay-container">
      <div class="hotel-stay-title">
        {{
          props.selectedStay.selectionType ===
          StaySelectionType.ManuallySelected
            ? $t('planner.selected_room')
            : $t('planner.suggested_room')
        }}
      </div>

      <HotelStay
        :stay="props.selectedStay"
        :show-select-button="
          props.selectedStay.selectionType === StaySelectionType.Suggested
        "
        :source="props.source"
        @more-information="emit('more-information', props.selectedStay)"
        @stay-selected="emit('stay-selected', $event)"
        @open-gallery="emit('open-gallery', $event)"
      />
    </div>

    <div v-if="props.alternativeStays.length" class="hotel-stay-container">
      <div class="hotel-stay-title">
        {{
          props.selectedStay
            ? $t('planner.more_alternatives')
            : $t('planner.select_room')
        }}
      </div>
      <HotelStay
        v-for="(stay, index) in props.alternativeStays"
        :key="index"
        :show-select-button="true"
        :stay="stay"
        :source="props.source"
        @stay-selected="emit('stay-selected', $event)"
        @more-information="emit('more-information', stay)"
        @open-gallery="emit('open-gallery', $event)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-details-stays-section {
  padding-bottom: var(--spacing-32);
  border-bottom-left-radius: var(--border-radius-8);
  border-bottom-right-radius: var(--border-radius-8);

  @include for-less-or-equal-than-1200-px {
    padding: var(--spacing-16) var(--spacing-16) var(--spacing-32)
      var(--spacing-16);
  }

  .hotel-stay-filters {
    display: flex;
    border-top: 1px solid var(--color-neutral-30);
    padding: var(--spacing-32) 0;
  }

  .hotel-stay-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);

    &:not(:last-child) {
      padding-bottom: var(--spacing-40);
    }

    .hotel-stay-title {
      color: var(--black);
      @include font-label-xl-light;
    }
  }
}
</style>
